import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Cluster visual config ────────────────────────────────────────────────────
const CLUSTER_META = {
  1:  { color: '#60A5FA', short: 'Foundation' },
  2:  { color: '#34D399', short: 'Finance'    },
  3:  { color: '#F59E0B', short: 'Tax'        },
  4:  { color: '#A78BFA', short: 'Inventory'  },
  5:  { color: '#F87171', short: 'HR'         },
  6:  { color: '#FB923C', short: 'Logistics'  },
  7:  { color: '#38BDF8', short: 'Sales/CRM'  },
  8:  { color: '#4ADE80', short: 'Banking'    },
  9:  { color: '#C084FC', short: 'Micro'      },
  10: { color: '#FCD34D', short: 'Construct.' },
  11: { color: '#67E8F9', short: 'AI'         },
  12: { color: '#FDA4AF', short: 'Integrity'  },
  13: { color: '#86EFAC', short: 'Feedback'   },
};

const LANG_META = {
  english:  { color: '#60A5FA', label: 'English'   },
  hindi:    { color: '#F87171', label: 'Hindi'      },
  hinglish: { color: '#FB923C', label: 'Hinglish'   },
  telugu:   { color: '#34D399', label: 'Telugu'     },
  telugish: { color: '#4ADE80', label: 'Telugish'   },
  tamil:    { color: '#F59E0B', label: 'Tamil'      },
  tamilish: { color: '#FCD34D', label: 'Tamilish'   },
  kannada:  { color: '#A78BFA', label: 'Kannada'    },
  marathi:  { color: '#C084FC', label: 'Marathi'    },
  gujarati: { color: '#38BDF8', label: 'Gujarati'   },
  punjabi:  { color: '#67E8F9', label: 'Punjabi'    },
  bengali:  { color: '#FDA4AF', label: 'Bengali'    },
  malayalam:{ color: '#86EFAC', label: 'Malayalam'  },
  odia:     { color: '#FEF08A', label: 'Odia'       },
  urdu:     { color: '#E2E8F0', label: 'Urdu'       },
  unknown:  { color: '#6B7280', label: 'Unknown'    },
};

// ─── Demo data generation ─────────────────────────────────────────────────────
const WEEKDAY_VOLS  = [30, 145, 110, 160, 130, 185, 75]; // Sun–Sat
const HOURLY_VOLS   = [3,2,1,1,1,3,8,18,30,42,38,25,20,18,28,32,30,26,38,45,42,35,18,10];
const C_WEIGHTS     = {1:4,2:18,3:4,4:8,5:10,6:14,7:8,8:5,9:3,10:24,11:2,12:3,13:2};
const C_TOTAL       = Object.values(C_WEIGHTS).reduce((a,b)=>a+b,0);

function pseudoRand(seed) {
  // Deterministic 0-1 from an integer seed
  return ((seed * 1664525 + 1013904223) & 0x7fffffff) / 0x7fffffff;
}

function generateDemoData(apiData) {
  const now = new Date();

  // 7-day
  const last_7_days_data = Array.from({ length: 7 }, (_, i) => {
    const d   = new Date(now - (6 - i) * 86400000);
    const dow = d.getDay();
    return {
      date:     d.toISOString().split('T')[0],
      day:      ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dow],
      messages: WEEKDAY_VOLS[dow],
    };
  });

  // 24-hour
  const last_24h_data = Array.from({ length: 24 }, (_, i) => {
    const h  = new Date(now - (23 - i) * 3600000);
    const hh = h.getHours();
    return {
      hour:     `${String(hh).padStart(2,'0')}:00`,
      messages: HOURLY_VOLS[hh],
    };
  });

  // By-cluster stacked
  const clusters   = apiData.by_cluster.clusters || Object.keys(CLUSTER_META).map(id=>({ id: +id, name: '' }));
  const by_cluster_data = last_7_days_data.map(day => {
    const row = { date: day.date, day: day.day };
    for (const c of clusters) {
      const w = C_WEIGHTS[c.id] || 1;
      row[`c${c.id}`] = Math.round((w / C_TOTAL) * day.messages);
    }
    return row;
  });

  // Agent usage — deterministic demo counts
  const agent_data = (apiData.agent_usage.data || []).map(a => {
    const w     = C_WEIGHTS[a.cluster_id] || 1;
    const noise = pseudoRand(a.agent_id);
    return { ...a, count: Math.max(1, Math.round(w * 8 * (0.55 + noise * 0.45))) };
  });
  agent_data.sort((a,b)=>b.count-a.count);
  agent_data.forEach((a,i)=>{ a.rank = i+1; });

  // Language
  const lang_data = [
    { language:'hinglish', count:450 },
    { language:'hindi',    count:280 },
    { language:'english',  count:180 },
    { language:'telugu',   count:95  },
    { language:'telugish', count:60  },
    { language:'tamil',    count:45  },
    { language:'kannada',  count:30  },
    { language:'marathi',  count:25  },
    { language:'gujarati', count:15  },
  ];

  return {
    ...apiData,
    last_7_days:          { data: last_7_days_data,  has_real_data: false },
    last_24_hours:        { data: last_24h_data,      has_real_data: false },
    by_cluster:           { ...apiData.by_cluster, data: by_cluster_data, has_real_data: false },
    agent_usage:          { data: agent_data,          has_real_data: false },
    language_distribution:{ data: lang_data,           has_real_data: false },
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const DemoLabel = () => (
  <div
    className="text-xs px-2 py-1 rounded-md mb-3 inline-block"
    style={{ background: 'rgba(251,146,60,0.12)', color: '#FB923C', border: '1px solid rgba(251,146,60,0.3)' }}
  >
    Demo data — no real conversations yet
  </div>
);

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-sm" style={{ background: '#1C1C1E', border: '1px solid #3A3A3C' }}>
      <p className="font-semibold text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.fill || p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon, label, value, sub }) => (
  <div
    className="rounded-2xl p-3 flex items-center gap-3"
    style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}
    data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}
  >
    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: 'rgba(255,45,85,0.12)' }}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs truncate" style={{ color: '#8E8E93' }}>{label}</p>
      <p className="text-lg font-bold leading-tight" style={{ color: '#F5F5F7' }}>{value}</p>
      {sub && <p className="text-xs" style={{ color: '#636366' }}>{sub}</p>}
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const navigate   = useNavigate();
  const [raw,      setRaw]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [tab,      setTab]      = useState('7d');
  const [search,   setSearch]   = useState('');
  const [showAll,  setShowAll]  = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const adminKey = process.env.REACT_APP_ADMIN_KEY || '';
      const res = await fetch(
        `${API_URL}/api/admin/analytics`,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      const data = await res.json();
      if (!res.ok || !data?.system_overview) {
        setError('Failed to load analytics.');
        return;
      }
      setRaw(data);
    } catch (e) {
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // ── Memos must come before any early returns ─────────────────────────────
  const demoData = useMemo(() => raw ? generateDemoData(raw) : null, [raw]);
  const data = useMemo(() => {
    if (!raw || !demoData) return null;
    return {
      ...raw,
      last_7_days:           raw.last_7_days.has_real_data           ? raw.last_7_days           : demoData.last_7_days,
      last_24_hours:         raw.last_24_hours.has_real_data         ? raw.last_24_hours         : demoData.last_24_hours,
      by_cluster:            raw.by_cluster.has_real_data            ? raw.by_cluster            : demoData.by_cluster,
      agent_usage:           raw.agent_usage.has_real_data           ? raw.agent_usage           : demoData.agent_usage,
      language_distribution: raw.language_distribution.has_real_data ? raw.language_distribution : demoData.language_distribution,
    };
  }, [raw, demoData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor:'transparent transparent #FF2D55 transparent' }} />
      </div>
    );
  }

  if (error || !raw || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#000' }}>
        <p style={{ color: '#F87171' }}>{error || 'Unknown error'}</p>
        <button onClick={fetchAnalytics} className="text-sm px-4 py-2 rounded-xl" style={{ background:'#1C1C1E', color:'#FF2D55', border:'1px solid #2C2C2E' }}>
          Retry
        </button>
      </div>
    );
  }

  const ov       = raw.system_overview;
  const clusters = raw.by_cluster.clusters || [];

  // ── Activity chart data ─────────────────────────────────────────────────────
  const chartData7d     = data.last_7_days.data;
  const chartData24h    = data.last_24_hours.data;
  const chartDataCluster= data.by_cluster.data;

  // Active clusters (those with any non-zero data in demo or real)
  const activeClusters = clusters.filter(c =>
    chartDataCluster.some(row => (row[`c${c.id}`] || 0) > 0)
  );

  // ── Agent table ─────────────────────────────────────────────────────────────
  const allAgents  = data.agent_usage.data || [];
  const maxCount   = allAgents[0]?.count || 1;
  const filtered   = search.trim()
    ? allAgents.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        (a.cluster_name||'').toLowerCase().includes(search.toLowerCase())
      )
    : allAgents;
  const displayed  = showAll ? filtered : filtered.slice(0, 20);
  const isSearching = search.trim().length > 0;

  // ── Language distribution ───────────────────────────────────────────────────
  const langData = data.language_distribution.data || [];
  const totalLang = langData.reduce((s,d)=>s+d.count, 0) || 1;

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen text-white pb-20"
      data-testid="admin-page"
      style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}
    >
      {/* ── Header ── */}
      <header
        className="sticky top-0 z-20 flex items-center justify-between px-4 py-3"
        style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}
      >
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity hover:opacity-70" style={{ background:'#1C1C1E' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-base font-bold tracking-tight">Admin Analytics</h1>
            <p className="text-xs" style={{ color:'#8E8E93' }}>Platform intelligence</p>
          </div>
        </div>
        <button onClick={fetchAnalytics} data-testid="admin-refresh-btn" className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity hover:opacity-70" style={{ background:'#1C1C1E' }}>
          <svg className="w-4 h-4" style={{ color:'#8E8E93' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </header>

      <div className="p-4 space-y-6">

        {/* ── System Overview ── */}
        <section data-testid="system-overview">
          <h2 className="text-sm font-semibold mb-3" style={{ color:'#8E8E93', textTransform:'uppercase', letterSpacing:'0.06em' }}>
            System Overview
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Businesses"
              value={ov.total_businesses}
              sub="registered"
              icon={<svg className="w-5 h-5" fill="none" stroke="#FF2D55" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            />
            <StatCard
              label="Convos Today"
              value={ov.conversations_today}
              sub="messages sent"
              icon={<svg className="w-5 h-5" fill="none" stroke="#FF2D55" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>}
            />
            <StatCard
              label="Active Users"
              value={ov.active_users_this_week}
              sub="this week"
              icon={<svg className="w-5 h-5" fill="none" stroke="#FF2D55" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
            />
            <StatCard
              label="Avg Response"
              value={ov.avg_response_time_ms > 0 ? `${(ov.avg_response_time_ms/1000).toFixed(1)}s` : '—'}
              sub="per message"
              icon={<svg className="w-5 h-5" fill="none" stroke="#FF2D55" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            />
          </div>
        </section>

        {/* ── Activity Charts ── */}
        <section data-testid="activity-charts">
          <h2 className="text-sm font-semibold mb-3" style={{ color:'#8E8E93', textTransform:'uppercase', letterSpacing:'0.06em' }}>
            Activity
          </h2>

          {/* Tab switcher */}
          <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background:'#1C1C1E' }}>
            {[
              { id:'7d',      label:'Last 7 Days'     },
              { id:'24h',     label:'Last 24 Hours'   },
              { id:'cluster', label:'By Cluster'      },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t.id ? 'linear-gradient(135deg,#FF2D55,#5856D6)' : 'transparent',
                  color: tab === t.id ? '#fff' : '#8E8E93',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Demo label (activity) */}
          {((tab==='7d' && !raw.last_7_days.has_real_data) ||
            (tab==='24h' && !raw.last_24_hours.has_real_data) ||
            (tab==='cluster' && !raw.by_cluster.has_real_data)) && <DemoLabel />}

          {/* 7d bar chart */}
          {tab === '7d' && (
            <div className="rounded-2xl p-3" style={{ background:'#1C1C1E', border:'1px solid #2C2C2E' }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData7d} margin={{ top:4, right:4, bottom:0, left:-20 }}>
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF2D55" />
                      <stop offset="100%" stopColor="#5856D6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill:'#8E8E93', fontSize:11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill:'#8E8E93', fontSize:10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="messages" fill="url(#barGrad)" radius={[4,4,0,0]} name="Messages" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 24h bar chart */}
          {tab === '24h' && (
            <div className="rounded-2xl p-3" style={{ background:'#1C1C1E', border:'1px solid #2C2C2E' }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData24h} margin={{ top:4, right:4, bottom:0, left:-20 }}>
                  <defs>
                    <linearGradient id="barGrad24" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34D399" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                  <XAxis
                    dataKey="hour"
                    tick={{ fill:'#8E8E93', fontSize:9 }}
                    axisLine={false}
                    tickLine={false}
                    interval={5}
                  />
                  <YAxis tick={{ fill:'#8E8E93', fontSize:10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="messages" fill="url(#barGrad24)" radius={[3,3,0,0]} name="Messages" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By-cluster stacked bar */}
          {tab === 'cluster' && (
            <div>
              <div className="rounded-2xl p-3" style={{ background:'#1C1C1E', border:'1px solid #2C2C2E' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartDataCluster} margin={{ top:4, right:4, bottom:0, left:-20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill:'#8E8E93', fontSize:11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill:'#8E8E93', fontSize:10 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomBarTooltip />} />
                    {activeClusters.map(c => (
                      <Bar
                        key={`c${c.id}`}
                        dataKey={`c${c.id}`}
                        stackId="a"
                        fill={(CLUSTER_META[c.id] || {}).color || '#888'}
                        name={(CLUSTER_META[c.id] || {}).short || c.name}
                        radius={activeClusters[activeClusters.length-1].id === c.id ? [3,3,0,0] : [0,0,0,0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Cluster legend */}
              <div className="grid grid-cols-3 gap-x-3 gap-y-2 mt-3">
                {activeClusters.map(c => {
                  const meta = CLUSTER_META[c.id] || { color:'#888', short: c.name };
                  return (
                    <div key={c.id} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: meta.color }} />
                      <span className="text-xs truncate" style={{ color:'#8E8E93' }}>{meta.short}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* ── Agent Usage Table ── */}
        <section data-testid="agent-usage-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold" style={{ color:'#8E8E93', textTransform:'uppercase', letterSpacing:'0.06em' }}>
              Agent Usage
            </h2>
            {!raw.agent_usage.has_real_data && <DemoLabel />}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color:'#636366' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              data-testid="agent-search-input"
              type="text"
              placeholder="Search agents or clusters…"
              value={search}
              onChange={e => { setSearch(e.target.value); setShowAll(false); }}
              className="w-full pl-9 pr-3 py-2 rounded-xl text-sm outline-none placeholder-gray-600"
              style={{ background:'#1C1C1E', border:'1px solid #2C2C2E', color:'#F5F5F7' }}
            />
          </div>

          {/* Table */}
          <div className="rounded-2xl overflow-hidden" style={{ background:'#1C1C1E', border:'1px solid #2C2C2E' }}>
            {/* Header */}
            <div className="grid grid-cols-[32px_1fr_80px_52px] gap-2 px-3 py-2 text-xs font-medium" style={{ color:'#636366', borderBottom:'1px solid #2C2C2E' }}>
              <span>#</span>
              <span>Agent</span>
              <span>Cluster</span>
              <span className="text-right">Msgs</span>
            </div>

            {displayed.length === 0 ? (
              <p className="text-center py-6 text-sm" style={{ color:'#636366' }}>No agents match your search</p>
            ) : (
              displayed.map((agent, idx) => {
                const meta   = CLUSTER_META[agent.cluster_id] || { color:'#888', short:'—' };
                const barPct = maxCount > 0 ? Math.max(4, (agent.count / maxCount) * 100) : 4;
                return (
                  <div
                    key={agent.agent_id}
                    data-testid={`agent-row-${agent.agent_id}`}
                    className="grid grid-cols-[32px_1fr_80px_52px] gap-2 items-center px-3 py-2.5"
                    style={{ borderBottom: idx < displayed.length - 1 ? '1px solid #2C2C2E' : 'none' }}
                  >
                    <span className="text-xs font-mono" style={{ color:'#636366' }}>
                      {agent.rank}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color:'#F5F5F7' }}>{agent.name}</p>
                      <div className="mt-1 h-1 rounded-full" style={{ background:'#2C2C2E' }}>
                        <div
                          className="h-1 rounded-full"
                          style={{ width:`${barPct}%`, background: meta.color, opacity:0.8 }}
                        />
                      </div>
                    </div>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-md text-center font-medium truncate"
                      style={{ background: `${meta.color}22`, color: meta.color, fontSize:'10px' }}
                    >
                      {meta.short}
                    </span>
                    <span className="text-right text-sm font-bold" style={{ color:'#F5F5F7' }}>
                      {agent.count}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Show all / less toggle */}
          {!isSearching && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs" style={{ color:'#636366' }}>
                {showAll ? `All ${filtered.length} agents` : `Top 20 of ${filtered.length} agents`}
              </p>
              <button
                data-testid="show-all-toggle"
                onClick={() => setShowAll(p => !p)}
                className="text-xs font-medium transition-opacity hover:opacity-70"
                style={{ color:'#FF2D55' }}
              >
                {showAll ? 'Show top 20' : `Show all ${filtered.length}`}
              </button>
            </div>
          )}
        </section>

        {/* ── Language Distribution ── */}
        <section data-testid="language-distribution">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold" style={{ color:'#8E8E93', textTransform:'uppercase', letterSpacing:'0.06em' }}>
              Language Distribution
            </h2>
            {!raw.language_distribution.has_real_data && <DemoLabel />}
          </div>

          <div className="rounded-2xl p-4" style={{ background:'#1C1C1E', border:'1px solid #2C2C2E' }}>
            {langData.length === 0 ? (
              <p className="text-center py-4 text-sm" style={{ color:'#636366' }}>No language data yet</p>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Donut */}
                <div className="flex justify-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={langData}
                      dataKey="count"
                      nameKey="language"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={48}
                      paddingAngle={2}
                    >
                      {langData.map((entry, i) => (
                        <Cell
                          key={entry.language}
                          fill={(LANG_META[entry.language] || { color: `hsl(${i*37},70%,60%)` }).color}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(val, name) => [`${val} msgs (${((val/totalLang)*100).toFixed(1)}%)`, (LANG_META[name]||{}).label || name]}
                      contentStyle={{ background:'#1C1C1E', border:'1px solid #3A3A3C', borderRadius:'12px', color:'#fff' }}
                    />
                  </PieChart>
                </div>

                {/* Legend */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {langData.map((entry) => {
                    const meta = LANG_META[entry.language] || { color:'#888', label: entry.language };
                    const pct  = ((entry.count / totalLang) * 100).toFixed(1);
                    return (
                      <div key={entry.language} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                          <span className="text-xs truncate" style={{ color:'#8E8E93' }}>{meta.label}</span>
                        </div>
                        <span className="text-xs font-semibold flex-shrink-0" style={{ color:'#F5F5F7' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

      </div>

      {/* ── Bottom Navigation ── */}
      <nav
        className="fixed bottom-0 left-0 right-0 px-4 py-2"
        data-testid="admin-bottom-nav"
        style={{ background:'#1C1C1E', borderTop:'1px solid #2C2C2E' }}
      >
        <div className="flex justify-around">
          {[
            { icon:'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', label:'Home',    path:'/dashboard' },
            { icon:'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label:'Day Book', path:'/daybook' },
            { icon:'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label:'Admin', path:'/admin', active: true },
          ].map(item => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center p-2"
              style={{ color: item.active ? '#FF2D55' : '#636366' }}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
